import { Link } from "@tanstack/react-router";
import React from "react";
import { AnimatedIcon } from "@patient-watch/icons";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { Separator } from "@patient-watch/ui/separator";
import { cn } from "@/lib/utils";

import type { ExtendedProfileT } from "@patient-watch/types";
import { useAuth } from "@/utils/auth";
import { getWebsiteHelpCentreAnchorProps } from "@/features/help/utils/websiteHelpCentre";
import { useUserLabelAccessQuery } from "../../auth/api/selectUserLabelAccess";
import { useUserTabAccessQuery } from "../api/selectUserTabAccess";
import { useProfileQuery } from "../../profile/api/selectProfile";
import useNavigationContent from "../hooks/useNavigationContent";
import { filterAppNavigation } from "../services/filterAppNavigation";
import NotificationCount from "./NotificationCount";
import ProfileNotificationCount from "./ProfileNotificationCount";
import ValidationNotificationCount from "./ValidationNotificationCount";
import WorklistNotificationCount from "./WorklistNotificationCount";
import { navigationGroupLabels, type PageT } from "./navigation-content";
import {
    checkIfSelected,
    getSharedNavigationStyles,
    showNotificationCount,
    showProfileNotificationCount,
    showValidationNotificationCount,
    showWorklistNotificationCount,
} from "./navigation-utils";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";

const MobileNavigation = () => {
    const auth = useAuth();
    const { data: profile } = useProfileQuery();
    const { data: userLabels = [] } = useUserLabelAccessQuery(auth.id);
    const { data: tabs = [] } = useUserTabAccessQuery(auth.id);
    const navigationContent = useNavigationContent();
    const copy = useProfileCopy();
    const { locale } = useAppLocale();
    const userLabelNames = userLabels.map((l) => l.label_name);
    const visibleNavigation = filterAppNavigation(navigationContent, tabs, userLabelNames);

    return (
        <>
            <ui.SheetHeader className="mt-8">
                <ui.SheetTitle className="px-3 font-medium text-slate-700 text-left">Patient Watch</ui.SheetTitle>
            </ui.SheetHeader>
            <div className="flex flex-col justify-center items-center gap-y-1 my-3 w-full">
                {visibleNavigation.map((page: PageT, index) => {
                    const selected = checkIfSelected(page.href ?? "");
                    const startsGroup = index === 0 || visibleNavigation[index - 1]?.group !== page.group;
                    const mobileButtonClasses = cn(
                        "flex justify-start items-center gap-x-2.5 mx-auto px-3 py-0 w-full [&_svg]:w-10 h-[52px] [&_svg]:h-10 min-h-[52px] font-medium text-md",
                        getSharedNavigationStyles(selected, true)
                    );
                    const icon = React.isValidElement(page.icon)
                        ? React.cloneElement(page.icon as React.ReactElement<{ size?: number }>, { size: 40 })
                        : page.icon;

                    return (
                        <React.Fragment key={page.href}>
                            {startsGroup && (
                                <div className={cn("w-full", index > 0 && "mt-2")}>
                                    {index > 0 && <Separator className="mb-3" />}
                                    <p className="px-3 font-medium text-slate-500 text-xs uppercase tracking-wide">
                                        {navigationGroupLabels[page.group]}
                                    </p>
                                </div>
                            )}
                            <ui.SheetClose asChild>
                                {page.onClick ? (
                                    <Button
                                        data-testid={`${page.href}-button`}
                                        className={mobileButtonClasses}
                                        variant="ghost"
                                        size="sm"
                                        onClick={page.onClick}
                                    >
                                        <span className="shrink-0">{icon}</span>
                                        {page.name}
                                        {showNotificationCount(page.href) && (
                                            <div className="ml-auto">
                                                <NotificationCount />
                                            </div>
                                        )}
                                        {showWorklistNotificationCount(page.href) && (
                                            <div className="ml-auto">
                                                <WorklistNotificationCount />
                                            </div>
                                        )}
                                        {showValidationNotificationCount(page.href) && (
                                            <div className="ml-auto">
                                                <ValidationNotificationCount />
                                            </div>
                                        )}
                                        {showProfileNotificationCount(page.href) && (
                                            <div className="ml-auto">
                                                <ProfileNotificationCount />
                                            </div>
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        data-testid={`${page.href}-button`}
                                        asChild
                                        className={mobileButtonClasses}
                                        variant="ghost"
                                        size="sm"
                                    >
                                        <Link to={page.href}>
                                            <span className="shrink-0">{icon}</span>
                                            {page.name}
                                            {showNotificationCount(page.href) && (
                                                <div className="ml-auto">
                                                    <NotificationCount />
                                                </div>
                                            )}
                                            {showWorklistNotificationCount(page.href) && (
                                                <div className="ml-auto">
                                                    <WorklistNotificationCount />
                                                </div>
                                            )}
                                            {showValidationNotificationCount(page.href) && (
                                                <div className="ml-auto">
                                                    <ValidationNotificationCount />
                                                </div>
                                            )}
                                            {showProfileNotificationCount(page.href) && (
                                                <div className="ml-auto">
                                                    <ProfileNotificationCount />
                                                </div>
                                            )}
                                        </Link>
                                    </Button>
                                )}
                            </ui.SheetClose>
                        </React.Fragment>
                    );
                })}
                {tabs.includes("help") ? (
                    <ui.SheetClose asChild>
                        <Button
                            data-testid="help-button"
                            asChild
                            className={cn(
                                "flex justify-start items-center gap-x-2.5 mx-auto px-3 py-0 w-full h-[52px] min-h-[52px] font-medium text-md",
                                getSharedNavigationStyles(false, true)
                            )}
                            variant="ghost"
                            size="sm"
                        >
                            <a
                                className="[&_svg]:w-10 [&_svg]:h-10"
                                {...getWebsiteHelpCentreAnchorProps(auth.roles, locale)}
                            >
                                <span className="shrink-0">
                                    <AnimatedIcon animation="question-speech-bubble" size={40} />
                                </span>
                                {copy.help}
                            </a>
                        </Button>
                    </ui.SheetClose>
                ) : null}
                <ui.SheetFooter className="mt-4">
                    <ui.SheetDescription className="text-left">
                        {copy.contactSupportPrompt}{" "}
                        <a
                            target="_blank"
                            rel="noreferrer"
                            className={cn("inline-block text-indigo-600 underline underline-offset-4")}
                            href="mailto:support@patient-watch.com"
                        >
                            {copy.contactSupport}
                        </a>
                        .
                    </ui.SheetDescription>
                </ui.SheetFooter>
            </div>
        </>
    );
};

export default MobileNavigation;
